/**
 * The shape yup 0.28 reports for one field from `describe()`.
 *
 * <p>`optional` is always null in this version, so requiredness has to be read
 * from the test list rather than from a flag.
 */
interface DescribedField {
  readonly tests?: readonly { readonly name?: string }[];
}

const hasRequiredTest = (field: unknown): boolean => {
  const tests = (field as DescribedField | undefined)?.tests;

  if (!Array.isArray(tests)) {
    return false;
  }

  return tests.some((test) => test?.name === 'required');
};

const isEmpty = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  return false;
};

/**
 * The required field names in a described yup field map.
 *
 * <p>The panels' completion badges read their required list from the same
 * schemas that validate the forms, so a change to validation cannot leave a
 * badge claiming a panel is complete while its form refuses to submit.
 */
export const requiredFieldsOf = (fields: Record<string, unknown>): string[] =>
  Object.keys(fields).filter((key) => hasRequiredTest(fields[key]));

/** How many of the field map's required fields have no value in `values`. */
export const countMissingRequired = (
  fields: Record<string, unknown>,
  values: Record<string, unknown>
): number =>
  requiredFieldsOf(fields).filter((key) => isEmpty(values[key])).length;
